/**
 * Session-scoped VOIP quality counters (AVS-4419).
 *
 * The 2026-09-04 multi-party call left exactly one durable artifact, `voipUsed: true` — a flawless
 * call and a failed one wrote byte-identical analytics. These tallies are the minimum that turns
 * the next incident into evidence: they ride the existing session analytics event as flat numbers
 * (`utils/ui/analytics.ts`) and are readable live through `syncTelemetry().voipQuality` in the
 * browser console.
 *
 * ## Scope: one session, not one tab
 *
 * Unlike `./syncTelemetry.ts`, whose counters are tab-lifetime and deliberately NOT cleared on a
 * session switch, these describe ONE session. They are reset at the two new-session-id seams
 * (`resetSessionSyncState`, called by `loadPublicSession` and `startNewSession`) and again right
 * after the SESSION_ENDED analytics payload has been built. That opposite lifetime is why they live
 * in their own module instead of beside the socket counters. The reset is NOT keyed on
 * `session:start`: `startNewSession` re-emits that event for an already-active session, which
 * happens on every car change during a live call.
 *
 * ## Hard constraints (machine-checked by `voipQuality.test.ts`)
 *
 * 1. **Imports nothing.** `webrtc/index.ts`, `utils/ui/rtc.ts`, `utils/ui/analytics.ts` and the
 *    session state all import this module, and `state/session` ↔ `utils/network` is a real cycle;
 *    an import-free leaf can never close it.
 * 2. **No browser globals.** This graph is evaluated in vitest-node and lambda contexts.
 * 3. **Every export is a `function` declaration** — hoisting has to survive any load ordering.
 * 4. **No clock.** These are pure tallies.
 * 5. **No PII.** The snapshot is numbers only. Per-peer verdicts are keyed by the API Gateway
 *    connection id the RTC store already holds, and that key never leaves this module.
 */

/**
 * Cap on distinct peers tracked for per-peer verdicts. A 7-party mesh with rejoins is well under
 * 20; beyond the cap a new peer still counts towards `statsSamples` and the RTT maximum but earns
 * no verdict, so a pathological rejoin storm cannot grow this map without bound.
 */
export const MAX_TRACKED_PEERS = 64;

/**
 * Samples a peer needs before its zero-frames verdict counts. At the sampler's 5 s cadence that is
 * roughly 5-10 s after the peer was first seen — long enough that a peer still negotiating, or one
 * that joined seconds before the session ended, is not called black.
 */
export const MIN_SAMPLES_FOR_VERDICT = 2;

/** One `getStats()` reading of one peer, projected to what the verdicts need (`utils/ui/voipStats.ts`). */
export interface IVoipPeerSample {
  /** The selected candidate pair uses a TURN relay on either side — "did the VPN force TURN?". */
  relay: boolean;
  /** `currentRoundTripTime` in milliseconds; negative when no pair is selected yet. */
  roundTripMs: number;
  /** Cumulative inbound video `framesDecoded`. */
  framesDecoded: number;
  /** A `MediaStream` exists for the peer (`ontrack` fired). Without one the tile is GRAY, not black. */
  hasStream: boolean;
  /** The app believes the peer's video is on (signaled `videoMuted !== true`). */
  videoExpected: boolean;
}

export interface IVoipQualitySnapshot {
  /** High-water mark of remote peers seen at once, plus this participant. Ghost peers count — that is the signal. */
  peakParticipants: number;
  /** Peers the DTLS rebuild ladder wrote off (`markTransportFailed`). The ICE ladder does not write off. */
  peersWrittenOff: number;
  /** ICE restarts attempted, across all peers. */
  iceRestarts: number;
  /** Times the ICE-restart budget was exhausted for a peer — once per exhaustion, latched. */
  iceRestartGiveUps: number;
  /** DTLS transport rebuilds attempted, across all peers. */
  transportRebuilds: number;
  /** Distinct peers whose selected pair was ever a relay on either side. */
  relayOnlyPeers: number;
  /** Maximum selected-pair RTT observed, whole milliseconds. `0` until any pair reported one. */
  maxObservedRttMs: number;
  /**
   * Distinct peers that had a stream, whose video the app expected, and that after
   * `MIN_SAMPLES_FOR_VERDICT` samples still had decoded no frame — "negotiated but never carried
   * video", the black-tile-that-is-our-bug signal. The LAST reading decides, so a slow start that
   * eventually decodes is not counted.
   */
  peersWithZeroFramesDecoded: number;
  /** Successful `getStats()` reads. The liveness heartbeat: read this before trusting any zero above. */
  statsSamples: number;
  /** `getStats()` reads that rejected. Console-probe only; not shipped. */
  statsReadFailures: number;
}

interface IPeerVerdict {
  relay: boolean;
  samples: number;
  lastFramesDecoded: number;
  lastHasStream: boolean;
  videoEverExpected: boolean;
}

interface IVoipQualityCounters {
  peakParticipants: number;
  peersWrittenOff: number;
  iceRestarts: number;
  iceRestartGiveUps: number;
  transportRebuilds: number;
  maxObservedRttMs: number;
  statsSamples: number;
  statsReadFailures: number;
}

function createCounters(): IVoipQualityCounters {
  return {
    peakParticipants: 0,
    peersWrittenOff: 0,
    iceRestarts: 0,
    iceRestartGiveUps: 0,
    transportRebuilds: 0,
    maxObservedRttMs: 0,
    statsSamples: 0,
    statsReadFailures: 0,
  };
}

let counters: IVoipQualityCounters = createCounters();

/** Per-peer verdicts, keyed by connection id. Internal only — the snapshot exposes counts. */
let peers: Map<string, IPeerVerdict> = new Map();

/**
 * The number of remote peers currently tracked. Called on every sampler tick; only the high-water
 * mark is kept, so a peer leaving never lowers it.
 */
export function recordVoipPeerCount(remotePeers: number): void {
  if (!Number.isInteger(remotePeers) || remotePeers < 0) {
    return;
  }

  counters.peakParticipants = Math.max(counters.peakParticipants, remotePeers + 1);
}

/** An ICE restart was attempted for a peer. */
export function recordVoipIceRestart(): void {
  counters.iceRestarts += 1;
}

/** A peer exhausted its ICE-restart budget. The caller latches this to once per exhaustion. */
export function recordVoipIceRestartGiveUp(): void {
  counters.iceRestartGiveUps += 1;
}

/** A DTLS transport rebuild was attempted for a peer. */
export function recordVoipTransportRebuild(): void {
  counters.transportRebuilds += 1;
}

/** The rebuild ladder gave up on a peer and wrote its transport off. */
export function recordVoipPeerWrittenOff(): void {
  counters.peersWrittenOff += 1;
}

/**
 * One successful `getStats()` reading of one peer.
 *
 * Every reading counts towards `statsSamples` and the RTT maximum. The per-peer verdict is kept
 * under `peerKey` and updated in place: relay is sticky (once relayed, always counted), "video
 * expected" is sticky (a camera turned on at any point means frames were owed), and the frame
 * count and stream presence are the latest reading, because the verdict is about how the peer
 * ended up.
 */
export function recordVoipPeerSample(peerKey: string, sample: IVoipPeerSample): void {
  counters.statsSamples += 1;

  if (Number.isFinite(sample.roundTripMs) && sample.roundTripMs > counters.maxObservedRttMs) {
    counters.maxObservedRttMs = Math.round(sample.roundTripMs);
  }

  const verdict = peers.get(peerKey);

  if (!verdict) {
    if (peers.size >= MAX_TRACKED_PEERS) {
      return;
    }

    peers.set(peerKey, {
      relay: sample.relay,
      samples: 1,
      lastFramesDecoded: sample.framesDecoded,
      lastHasStream: sample.hasStream,
      videoEverExpected: sample.videoExpected,
    });
    return;
  }

  verdict.relay = verdict.relay || sample.relay;
  verdict.samples += 1;
  verdict.lastFramesDecoded = sample.framesDecoded;
  verdict.lastHasStream = sample.hasStream;
  verdict.videoEverExpected = verdict.videoEverExpected || sample.videoExpected;
}

/** A `getStats()` read rejected — the pc closed under the read, or is not a real pc. */
export function recordVoipStatsReadFailure(): void {
  counters.statsReadFailures += 1;
}

/** Whether a tracked peer, as last seen, is the black-tile signal. */
function isZeroFramesVerdict(verdict: IPeerVerdict): boolean {
  return (
    verdict.samples >= MIN_SAMPLES_FOR_VERDICT &&
    verdict.lastFramesDecoded === 0 &&
    verdict.lastHasStream &&
    verdict.videoEverExpected
  );
}

/** A copy — mutating the result cannot corrupt the internal counters. Numbers only. */
export function voipQualitySnapshot(): IVoipQualitySnapshot {
  let relayOnlyPeers = 0;
  let peersWithZeroFramesDecoded = 0;

  peers.forEach((verdict) => {
    if (verdict.relay) {
      relayOnlyPeers += 1;
    }

    if (isZeroFramesVerdict(verdict)) {
      peersWithZeroFramesDecoded += 1;
    }
  });

  return {
    peakParticipants: counters.peakParticipants,
    peersWrittenOff: counters.peersWrittenOff,
    iceRestarts: counters.iceRestarts,
    iceRestartGiveUps: counters.iceRestartGiveUps,
    transportRebuilds: counters.transportRebuilds,
    relayOnlyPeers,
    maxObservedRttMs: counters.maxObservedRttMs,
    peersWithZeroFramesDecoded,
    statsSamples: counters.statsSamples,
    statsReadFailures: counters.statsReadFailures,
  };
}

/**
 * Start a new session's tally. Called from `resetSessionSyncState` (the new-session-id seams),
 * after the SESSION_ENDED analytics payload is built, and from `utils/test/resetAppState`.
 */
export function resetVoipQuality(): void {
  counters = createCounters();
  peers = new Map();
}
